import appConfig from 'appConfig';
import { dispatch } from 'pluginServices/store';
import { pushToast, removeToast } from 'containers/ToastManager/actions';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import PublicationResolver from 'resolvers/RTMResolvers/PublicationResolver';
import exportService from 'services/ExportService';
import projectService from 'services/ProjectService';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';

import actionMessages from 'Workspace/Publications/components/PublicationListItemActions/messages';
import publishMessages from 'Workspace/Publications/components/PublishStatus/messages';
import { permissionTypes } from 'Workspace/Publications/utils/constants';
import { download, sortByDate } from 'Workspace/Publications/utils';


/**
 * Builds a service to access projects, and hold all of their business
 * logic.
 */
export default (() => {
  /**
   * @type {PublicationResolver}
   */
  let publicationResolver;
  let isRegistered = false;

  const _subscriptions = {};
  const _rtmUnsubscribeFunctions = {};

  /**
   * @desc Creates a publication record
   * @param {Object} publicationDetails
   */
  const _createPublication = async (publicationDetails) => {
    const response = await publicationResolver.createPublication(publicationDetails);
    return response;
  };

  /**
   * @desc Trigger publication export
   * @param {Object} payload
   */
  const publish = (payload, exportListener, publishListener) => (
    {
      EPUB_ETEXT: publishEpub,
      EPUB_CHANNEL_PARTNER: publishEpub,
      BRONTE_CLASSES_ON_DEMAND: publishClassOnDemand,
      BRONTE_PEARSON_PLUS: publishPearsonPlus,
      BRONTE_PEARSON_PLUS_TEST_ONLY: pearsonPlusTestOnly,
      IN_DESIGN: publishEpub,
    }[payload.format](payload, exportListener, publishListener)
  );

  const isPublicationOwner = async (activityId) => {
    const projectDetails = await projectService.getProjectDetails(activityId, "ACTIVITY");
    // getProjectDetails might return a 401, no permissions, if the project is not visible to you.
    if (!projectDetails.id) {
      return false;
    } else {
      const { permissionLevel } = await projectService.getProject(projectDetails.id);
      return permissionLevel === permissionTypes.OWNER ? true : false;
    }
  };

  const publishEpub = async (payload, exportListener, publishListener) => {
    const { exportElementsCount, exportId } = await publicationResolver.publish(payload);
    exportService.subscribeToExport(exportId, exportListener);
    const { publicationId } = await _createPublication({ format: payload.format, ...payload.publication, exportId });
    subscribeToPublicationJob(publicationId, publishListener);
    return exportElementsCount;
  };

  /**
   * Deletes a publication history item
   * @param {publication Object} payload
   * @returns {object}
   */
  const deletePublication = async (payload) => {
    const response = await publicationResolver.deletePublication(payload);
    if (response.error) {
      dispatch(pushToast({ type: 'simple', message: response.error.message }));
    }
    return response;
  };

  const publishClassOnDemand = async (payload, exportListener, publishListener) => {
    //Create publication and get publicationId
    const { publicationId } = await _createPublication({
      format: payload.format,
      ...payload.publication,
    });
    //Subscribe to PublicationJob with publicationId 
    await subscribeToPublicationJob(publicationId, publishListener);
    payload.publication.publicationId = publicationId;
    //Do the actual publish.
    const response = await publicationResolver.publish(payload);

    if (response?.noCoursewareChanges) {
      dispatch(pushToast({ message: intl.formatMessage(publishMessages.CONTENT_NOT_CHANGED) }));
      return;
    }

    if (response.error) {
      dispatch(pushToast({ type: "simple", message: response.error.message }));
      return response;
    }
  };

  const isMasteringProductIdUniqueService = async (payload) => {
    const response = await publicationResolver.validateProductId(payload);
    if (response.error) {
      dispatch(pushToast({ type: "simple", message: response.error.message }));
    }
    return response;
  };

  const publishPearsonPlus = async (payload, exportListener, publishListener) => {
    const { activityId, metadata, market } = payload.publication;
    const { publications } = await publicationResolver.fetchPublicationHistory(activityId, payload.format, market);
    let response;
    // add toc level to publication payload
    payload.publication.tocLevel = metadata.tocLevel;
    // First publish or rePublish PearsonPlus Publication.
    if (publications?.length) {
      const sortedPublications = sortByDate(publications);
      let { bookId = '', cohortId, deploymentId } = sortedPublications[0];

      // Check if bookId is empty and try to find a non-empty bookId
      const nonEmptyBookId = bookId || sortedPublications.find((publication) => publication.bookId)?.bookId || '';

      if (nonEmptyBookId === '') {
        response = await publicationResolver.publish(payload);
      } else {
        response = await publicationResolver.rePublishPearsonPlus({ ...payload.publication, bookId: nonEmptyBookId, cohortId, deploymentId });
      }
    } else {
      response = await publicationResolver.publish(payload);
    }

    if (response.error) {
      dispatch(pushToast({ type: "simple", message: response.error.message, waitForAction: true, actions: [{ text: '⛌', fn: () => { } }], toastId: activityId }));
      return 0;
    }
    // After publish/republish export publication.
    const showTocLevel = featureFlagService.is(featureKeys.WORKSPACE_PUBLICATION_THREE_LEVEL_TOC_OPTION_BV);

    const { cohortId, deploymentId, productId: reviewProductId } = response;
    const exportMetadata = JSON.stringify({
      cohortId,
      deploymentId,
      productId: reviewProductId,
      ...(showTocLevel && { tocLevel: metadata.tocLevel }),
      ...(market && { market }),
    });
    const { exportElementsCount, exportId } = await publicationResolver.exportPearsonPlus(activityId, exportMetadata, market);
    //After export subscribe and create.
    exportService.subscribeToExport(exportId, exportListener);
    const { publicationId } = await _createPublication({
      format: payload.format,
      exportId,
      reviewProductId,
      ...payload.publication,
    });
    subscribeToPublicationJob(publicationId, publishListener);
    return exportElementsCount;
  };
  //publishBronteViewerTestOnly
  const pearsonPlusTestOnly = async (payload, exportListener, publishListener) => {
    const { activityId, productId } = payload.publication;
    const { publications } = await publicationResolver.fetchPublicationHistory(activityId, payload.format);
    let response;
    if (publications && publications.length) {
      const { cohortId, deploymentId } = publications[0];
      response = await publicationResolver.rePublishPearsonPlusTestOnly({ ...payload.publication, cohortId, deploymentId });
    } else {
      response = await publicationResolver.publish(payload);
    }
    if (response.error) {
      dispatch(pushToast({ type: "simple", message: response.error.message }));
      return;
    }
    // After publish/republish export publication.
    const { cohortId, deploymentId } = response;
    const metadata = JSON.stringify({
      cohortId,
      deploymentId,
      productId,
    });
    const { exportElementsCount, exportId } = await publicationResolver.exportPearsonPlusTest(activityId, metadata);
    //After export subscribe and create.
    exportService.subscribeToExport(exportId, exportListener);
    const { publicationId } = await _createPublication({
      format: payload.format,
      exportId: exportId,
      ...payload.publication,
    });
    subscribeToPublicationJob(publicationId, publishListener);
    return exportElementsCount;
  };

  const productIdPDZ = async (payload) => await publicationResolver.validatePDZProductId(payload);

  /**
   * @description Check if Publication Item is Added
   * @param {array} publicationList
   * @param {object} newPublicationItem
   * @returns {boolean}
   */
  const checkIfPublicationItemAdded = (publicationList, newPublicationItem) => {
    const publicationMarketsMatch = (publicationItem) => {
      const existing = publicationItem?.market;
      const incoming = newPublicationItem?.market;
      if (existing == null && incoming == null) return true;
      if (existing == null || incoming == null) return false;
      return existing === incoming;
    };
    const publicationListItemIndex = publicationList.findIndex((publicationItem) =>
      publicationItem.activityId === newPublicationItem.activityId &&
      publicationItem.outputType === newPublicationItem.outputType &&
      publicationMarketsMatch(publicationItem)
    );
    return publicationListItemIndex > -1;
  };

  const getOculusStatus = async (bookId) => {
    const response = await publicationResolver.getOculusStatus(bookId);
    return response;
  };

  const getCoursewarePublicationStatus = async (activityId, publicationOutputType) => {
    const response = await publicationResolver.getCoursewarePublicationStatus({
      activityId,
      publicationOutputType,
    });
    return response;
  };

  const provisionExternalUser = async (userDetails) => {
    const response = await publicationResolver.provisionExternalUser(userDetails);
    return response;
  };

  /**
  * @description Check if a specific element has been published
  * @param {string} activityId
  * @param {string} elementId
  * @returns {object}
  */
  const isElementPublished = async (activityId, elementId) => {
    const response = await publicationResolver.isElementPublished({
      activityId,
      elementId,
    });
    return response;
  };

  /**
   * @description Construct URL and headers
   * @param {string} exportId
   * @returns {object}
   */
  const _getURlandHeader = (exportId) => {
    const s3Folder = exportId.replace(/-/g, '');
    const s3Bucket = appConfig.getEpubURL();
    const REPORT_FILE_PATH = 'reports/Report.xlsx';
    const url = `${s3Bucket}/${s3Folder}/${REPORT_FILE_PATH}`;
    const headers = new Headers();
    headers.append('Range', 'bytes=0-1');
    return {
      url,
      header: {
        method: 'GET',
        headers,
      },
    };
  };

  const getPublicationHistory = async (activityId, outputType, market) => (await publicationResolver.fetchPublicationHistory(activityId, outputType, market));

  const getIsOnDemand = async (cohortId, deploymentId) => await publicationResolver.fetchIsOnDemand(cohortId, deploymentId);

  const getDeploymentDetails = async (cohortId, deploymentId) => await publicationResolver.fetchDeploymentDetails(cohortId, deploymentId);

  const getPublicationVersionDiff = async (activityId, publicationVerAId, publicationVerBId) => (await publicationResolver.fetchPublicationVersionDifference(activityId, publicationVerAId, publicationVerBId));

  /**
   * @description updates publication title
   * @param {string} activityId
   * @param {string} title
   * @param {string} version
   * @returns {object}
   */
  const updatePublicationTitle = async (activityId, title, version) =>
    (await publicationResolver.publicationTitleUpdate(activityId, title, version));

  /**
   * @description ping the S3 location to see if the report folder is generated
   * Its wrapped in an IIFE to ensure the first call happens immediately
   * All subsequent pings will happen in 5s intervals
   * Pings will be cancelled after 10 minutes
   * @param {object}
   * @param {string} exportId
   * @param {string} title
   */
  const checkIfReportGenerated = async ({ activityId, exportId, title }, onCompleteDownloadReportCallback) => {
    const THIRTY_MINUTE = 30 * 60 * 1000;
    const FIVE_SECOND = 5 * 1000;
    const { url, header } = _getURlandHeader(exportId);
    let pingInterval = setInterval((function ping() {
      fetch(url, header).then((response) => {
        if ([200, 206].includes(response.status)) {
          clearInterval(pingInterval);
          pingInterval = undefined;
          // perform download of file
          download(
            url,
            title,
            'xlsx',
            () => {
              dispatch(removeToast({ toastId: activityId }));
              onCompleteDownloadReportCallback();
            });
        }
      }).catch((error) => {
        clearInterval(pingInterval);
        pingInterval = undefined;
        dispatch(pushToast({ message: intl.formatMessage(actionMessages.reportFailed) }));
        dispatch(removeToast({ toastId: activityId }));
        throw new Error(`ePub error report generation failed, ${error}.`);
      });
      return ping;
    }()), FIVE_SECOND);

    setTimeout(() => {
      if (pingInterval) {
        clearInterval(pingInterval);
        dispatch(pushToast({ message: intl.formatMessage(actionMessages.reportFailed) }));
        dispatch(removeToast({ toastId: activityId }));
        throw new Error('ePub error report generation timed out');
      }
    }, THIRTY_MINUTE);
  };

  const unSubscribeToPublicationJob = (publicationId) => {
    if (_subscriptions[publicationId]) {
      _subscriptions[publicationId] = null;
    }
    _forgetPublicationJob(publicationId);
  };

  function _forgetPublicationJob(publicationId) {
    if (_rtmUnsubscribeFunctions[publicationId]) {
      // remove subscription from requestTable of RTM and
      // then the RTM calls author.job.unsubscribe
      _rtmUnsubscribeFunctions[publicationId]();
      _rtmUnsubscribeFunctions[publicationId] = null;
    }
  }

  function _createUnsubscribe(publicationId, listener) {
    return () => {
      const listenerIndex = _subscriptions[publicationId].indexOf(listener);
      _subscriptions[publicationId].splice(listenerIndex, 1);
      if (_subscriptions[publicationId].length === 0) {
        _forgetPublicationJob(publicationId);
      }
    };
  }

  /**
   * Creates a subscription to an job.
   * @param {string} publicationId
   * @param {function(*)} listener A callback invoke on subscription,
   * and every change to the job afterwards
   */
  const subscribeToPublicationJob = async (publicationId, listener) => {
    _rtmUnsubscribeFunctions[publicationId] =
      await publicationResolver.publicationJobsSubscribe(publicationId, listener);

    _subscriptions[publicationId] = _subscriptions[publicationId] || [];
    _subscriptions[publicationId].push(listener);

    return _createUnsubscribe(publicationId, listener);
  };

  return {
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      publicationResolver = new PublicationResolver(connectionProvider);
    },
    checkIfReportGenerated,
    getPublicationHistory,
    checkIfPublicationItemAdded,
    unSubscribeToPublicationJob,
    subscribeToPublicationJob,
    getOculusStatus,
    getCoursewarePublicationStatus,
    provisionExternalUser,
    isPublicationOwner,
    isElementPublished,
    deletePublication,
    publish,
    updatePublicationTitle,
    isMasteringProductIdUniqueService,
    productIdPDZ,
    getIsOnDemand,
    getPublicationVersionDiff,
    getDeploymentDetails,
  };
})();
